import i18n from "../i18n";
import { apiEndpoint } from "../utils/env";

// The personal layer's client — one person's folders, what they have filed there, and the upload that
// puts a new document into the archive. Every call is scoped server-side to the caller's own token, so
// nothing here passes an owner: asking for somebody else's folder simply returns "not found".

async function call(path, token, options = {}) {
  const headers = options.headers || {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // The language the PRODUCT is in, which is not the browser's: the API names starter folders from this,
  // so a Turkish laptop running the app in English gets "My Documents". Accept-Language cannot be set from
  // fetch anyway, and would be the wrong answer if it could.
  const lang = i18n?.language;
  if (lang) headers["X-App-Language"] = lang;
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  }
  const res = await fetch(apiEndpoint(path), { ...options, headers });
  const txt = await res.text();
  let json = null;
  try {
    json = txt ? JSON.parse(txt) : null;
  } catch {
    json = txt;
  }
  if (!res.ok) {
    const message =
      (json && json.message) || (typeof json === "string" ? json : res.statusText);
    const error = new Error(message || `Request failed: ${res.status}`);
    error.code = (json && json.code) || null;
    error.status = res.status;
    error.details = json;
    throw error;
  }
  return json;
}

/**
 * The caller's workspace, provisioning it on the very first call.
 * `locale` names the starter folders — both the rows written on that very first call and, on every call
 * after it, the names they are read back under. A folder someone has renamed keeps their wording.
 */
export async function getWorkspaceTree(token, locale) {
  const q = locale ? `?locale=${encodeURIComponent(locale)}` : "";
  return call(`/api/Workspace/Tree${q}`, token, { method: "GET" });
}

export async function createWorkspaceFolder(token, { parentId, name, description, icon, sortOrder }) {
  return call("/api/Workspace/Folder", token, {
    method: "POST",
    body: JSON.stringify({ parentId, name, description, icon, sortOrder }),
  });
}

export async function renameWorkspaceFolder(token, id, { name, description }) {
  return call(`/api/Workspace/Folder/${id}`, token, {
    method: "PUT",
    body: JSON.stringify({ name, description }),
  });
}

export async function moveWorkspaceFolder(token, id, newParentId) {
  return call(`/api/Workspace/Folder/${id}/Move`, token, {
    method: "POST",
    body: JSON.stringify({ newParentId }),
  });
}

/** Archives a folder and its subtree. The documents filed inside are untouched. */
export async function archiveWorkspaceFolder(token, id) {
  return call(`/api/Workspace/Folder/${id}`, token, { method: "DELETE" });
}

/** The documents in one folder, each carrying its base, org folder and taxonomy terms. */
export async function getWorkspaceFolderDocuments(token, id, { includeSubtree = false, max } = {}) {
  const params = new URLSearchParams();
  if (includeSubtree) params.set("includeSubtree", "true");
  if (max) params.set("max", String(max));
  const q = params.toString();
  return call(`/api/Workspace/Folder/${id}/Documents${q ? `?${q}` : ""}`, token, { method: "GET" });
}

/** Files a document that is ALREADY archived — the drag from search results into a folder. */
export async function fileDocument(token, { personalFolderId, dbName, archiveNo, documentId, displayName }) {
  return call("/api/Workspace/File", token, {
    method: "POST",
    body: JSON.stringify({ personalFolderId, dbName, archiveNo, documentId, displayName }),
  });
}

// `filingId` throughout, never `itemId`: in a document row `itemId` is the encrypted link the viewer
// opens, and reusing the name for the membership row is what made an uploaded document unreachable.
export async function unfileDocument(token, filingId) {
  return call(`/api/Workspace/Filing/${filingId}`, token, { method: "DELETE" });
}

/**
 * Renames a document AS IT APPEARS IN THIS FOLDER — the filing's own label, not the archived record.
 * Pass an empty name to clear the label and go back to the file name it was archived under.
 */
export async function renameFiling(token, filingId, displayName) {
  return call(`/api/Workspace/Filing/${filingId}`, token, {
    method: "PUT",
    body: JSON.stringify({ displayName }),
  });
}

export async function moveFiling(token, filingId, targetFolderId) {
  return call("/api/Workspace/Filing/Move", token, {
    method: "POST",
    body: JSON.stringify({ filingId, targetFolderId }),
  });
}

export async function copyFiling(token, filingId, targetFolderId) {
  return call("/api/Workspace/Filing/Copy", token, {
    method: "POST",
    body: JSON.stringify({ filingId, targetFolderId }),
  });
}

// ── the recycle bin ──────────────────────────────────────────────────────────────────────────────
// Removing a document from a folder, or removing a folder, is reversible. The bin is the owner's, so
// what a colleague removes from a folder shared with them lands where the folder's owner can find it.

export async function getWorkspaceTrash(token) {
  return call("/api/Workspace/Trash", token, { method: "GET" });
}

/** Puts entries back. Partial: `blocked` names what could not come back, and why. */
export async function restoreFromTrash(token, { filingIds = [], folderIds = [] }) {
  return call("/api/Workspace/Trash/Restore", token, {
    method: "POST",
    body: JSON.stringify({ filingIds, folderIds }),
  });
}

/** Deletes bin entries for good — the FILINGS, never the archived documents they point at. */
export async function purgeFromTrash(token, { filingIds = [], folderIds = [] }) {
  return call("/api/Workspace/Trash/Purge", token, {
    method: "POST",
    body: JSON.stringify({ filingIds, folderIds }),
  });
}

export async function emptyTrash(token) {
  return call("/api/Workspace/Trash/Empty", token, { method: "POST" });
}

/** Which of my folders hold an open document — the detail pane's reverse lookup. */
export async function getDocumentFilings(token, { dbName, archiveNo, documentId }) {
  const params = new URLSearchParams({ dbName });
  if (archiveNo) params.set("archiveNo", archiveNo);
  else if (documentId != null) params.set("documentId", String(documentId));
  return call(`/api/Workspace/Document/Filings?${params}`, token, { method: "GET" });
}

/**
 * Uploads a file and archives it as a real document, then files it into a personal folder.
 * `folderId` and `taxonomyNodeIds` place the same document in the ORG layers at the same time, so one
 * upload can land in all three at once.
 */
export async function uploadToWorkspace(
  token, { file, personalFolderId, folderId, taxonomyNodeIds, documentDate }
) {
  const form = new FormData();
  form.append("file", file);
  form.append("personalFolderId", String(personalFolderId));
  if (folderId != null) form.append("folderId", String(folderId));
  if (taxonomyNodeIds && taxonomyNodeIds.length) {
    form.append("taxonomyNodeIds", taxonomyNodeIds.join(","));
  }
  if (documentDate) form.append("documentDate", documentDate);
  return call("/api/Workspace/Upload", token, { method: "POST", body: form });
}

export async function getFolderShares(token, folderId) {
  return call(`/api/Workspace/Folder/${folderId}/Shares`, token, { method: "GET" });
}

export async function addFolderShare(token, folderId, { granteeUserName, canWrite, includeSubtree }) {
  return call(`/api/Workspace/Folder/${folderId}/Shares`, token, {
    method: "POST",
    body: JSON.stringify({ granteeUserName, canWrite, includeSubtree }),
  });
}

export async function removeFolderShare(token, shareId) {
  return call(`/api/Workspace/Share/${shareId}`, token, { method: "DELETE" });
}
